/**
 * HistoHead wishlist: the list of saved items, and the hearts.
 *
 * One entry per product + colour:
 *   p  product id            h  product handle        v  variant id
 *   c  colour ('' when the product has no colour option)
 *   s  1 when the size was chosen (saved on the product page), 0 when saved from a card
 *   t  when it was added (unix seconds)
 *
 * window.hhWishlist is the only code that reads or writes the list; everything else calls it and
 * listens for the 'hh:wishlist:change' event.
 *
 * Where the list lives:
 *   - always in this browser (localStorage), so the hearts are right at once on every page;
 *   - for a logged-in customer also in their account (customer metafield histohead.wishlist), when the
 *     theme setting "Save the wishlist to the customer account" is on. The page carries the account's
 *     list (layout/theme.liquid, <script id="hh-wishlist-account">), and every change made here is sent
 *     to the relay behind the store's app proxy, which writes it to the account: the theme cannot write
 *     there by itself.
 *
 * A guest's list joins the account's list at the first login on this device. After that the browser
 * keeps a copy of the account's list; changes made while logged out wait here and are sent at the next
 * login of the same customer. Another customer logging in on this device gets their own list, not a mix.
 *
 * What happens to wishlists is also counted, for the reports in Studio (see "the counter" below): the
 * counts say what happened and to which product, never who did it.
 */
(function () {
  'use strict';

  if (window.hhWishlist) return;

  var KEY = 'hh:wishlist';
  var CHANGE = 'hh:wishlist:change';
  var PENDING_MAX = 400;
  var SEND_DELAY_MS = 300;
  var RETRY_FIRST_MS = 2000;
  var RETRY_MAX_MS = 60000;

  /*
    How a list changes. The same text lives in the relay (histohead-relay/src/wishlist.js), which applies
    the same changes to the account's copy; the relay's tests fail when the two texts differ.
  */
  /* shared-list-logic:begin */
  var HH_WL_MAX_ITEMS = 200;

  // One saved item, checked and tidied; null when it cannot be one
  function hhWlClean(raw, now) {
    if (!raw || typeof raw !== 'object') return null;
    var p = Number(raw.p);
    var v = Number(raw.v);
    var h = typeof raw.h === 'string' ? raw.h : '';
    var c = typeof raw.c === 'string' ? raw.c : '';
    var t = Math.floor(Number(raw.t));
    if (!(p > 0) || !(v > 0) || p % 1 !== 0 || v % 1 !== 0) return null;
    if (!h || h.length > 255 || /[\s\/?#]/.test(h)) return null;
    if (c.length > 100) return null;
    if (!(t > 0) || t > now + 300) t = now;
    return { p: p, h: h, v: v, c: c, s: raw.s ? 1 : 0, t: t };
  }

  // color null or undefined = any colour of the product
  function hhWlSame(item, productId, color) {
    if (Number(item.p) !== Number(productId)) return false;
    return color === undefined || color === null || (item.c || '') === color;
  }

  // One entry per product + colour. An add older than the entry already there changes nothing
  // (a list saved as a guest joins the account's list at login, each item with its own date).
  function hhWlAdd(list, op, now) {
    var entry = hhWlClean(op, now);
    if (!entry) return list;
    var out = [entry];
    for (var i = 0; i < list.length; i++) {
      if (!hhWlSame(list[i], entry.p, entry.c)) {
        out.push(list[i]);
      } else if (list[i].t > entry.t) {
        return list;
      }
    }
    return out;
  }

  function hhWlRemove(list, op) {
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (!hhWlSame(list[i], op.p, op.c)) out.push(list[i]);
    }
    return out;
  }

  // Changes an entry where it stands: another size (v, s) or another colour (c).
  // Refused when the new colour is already an entry of its own.
  function hhWlUpdate(list, op) {
    var to = op.to && typeof op.to === 'object' ? op.to : {};
    var from = typeof op.c === 'string' ? op.c : '';
    var color = typeof to.c === 'string' ? to.c : from;
    var variant = Number(to.v);
    var i;
    if (color.length > 100) return list;
    if (color !== from) {
      for (i = 0; i < list.length; i++) {
        if (hhWlSame(list[i], op.p, color)) return list;
      }
    }
    var out = [];
    for (i = 0; i < list.length; i++) {
      var item = list[i];
      if (hhWlSame(item, op.p, from)) {
        item = { p: item.p, h: item.h, v: item.v, c: color, s: item.s, t: item.t };
        if (variant > 0 && variant % 1 === 0) item.v = variant;
        if (to.s !== undefined && to.s !== null) item.s = to.s ? 1 : 0;
      }
      out.push(item);
    }
    return out;
  }

  /*
    Applies changes to a list and gives the new list: newest first, HH_WL_MAX_ITEMS entries at most.
      { o: 'add', p, h, v, c, s, t }
      { o: 'remove', p, c }                  c left out = every colour of the product
      { o: 'update', p, c, to: { c, v, s } }
    Anything that is not a valid entry or change is left out.
  */
  function hhWlApply(items, ops, now) {
    var list = [];
    var i;
    for (i = 0; i < items.length; i++) {
      var kept = hhWlClean(items[i], now);
      if (kept) list.push(kept);
    }
    for (i = 0; i < ops.length; i++) {
      var op = ops[i];
      if (!op || typeof op !== 'object') continue;
      if (op.o === 'add') list = hhWlAdd(list, op, now);
      else if (op.o === 'remove') list = hhWlRemove(list, op);
      else if (op.o === 'update') list = hhWlUpdate(list, op);
    }
    list.sort(function (a, b) {
      return b.t - a.t;
    });
    if (list.length > HH_WL_MAX_ITEMS) list.length = HH_WL_MAX_ITEMS;
    return list;
  }
  /* shared-list-logic:end */

  function now() {
    return Math.floor(Date.now() / 1000);
  }

  function newId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  /* ---------- the visitor's account, as the page tells it ---------- */

  // enabled: account saving is switched on; customer: id of the logged-in customer, or null;
  // list: the account's list when the page was made; url: the relay; login: where to log in
  var account = (function () {
    var info = { enabled: false, customer: null, list: null, url: '', login: '' };
    var element = document.getElementById('hh-wishlist-account');
    if (!element) return info;
    var data = {};
    try {
      data = JSON.parse(element.textContent || '{}') || {};
    } catch (e) {
      // Unreadable: the visitor counts as a guest
    }
    info.enabled = true;
    info.customer = Number(data.customer) > 0 ? Number(data.customer) : null;
    info.list = data.list && typeof data.list === 'object' ? data.list : null;
    info.url = element.getAttribute('data-url') || '';
    info.login = element.getAttribute('data-login') || '';
    return info;
  })();

  /* ---------- the counter ---------- */

  /*
    Theme setting "Count what happens to wishlists": a save, a removal, a visit to the wishlist page and
    a saved product going to the cart are reported to the relay, which keeps the count for the reports
    in Studio (layout/theme.liquid, <meta name="hh-wishlist-stats">). A report says what happened and to
    which product, never who did it: it carries no visitor or customer number, and nothing is stored in
    the browser for it.

    Nothing is reported from the theme editor, nor from a browser in which the store's owner opened any
    page with ?hh-count=off (remembered in that browser until a page is opened with ?hh-count=on).
  */
  var STATS_MUTE_KEY = 'hh:wishlist:nocount';
  var STATS_MAX = 20;
  var CART_MARK = '_hh_wishlist';
  var CART_MARK_MAX = 30;

  // url: where the reports go ('' = nothing is counted); muted: the owner's "do not count this browser"
  var stats = (function () {
    var info = { url: '', muted: false, queue: [], timer: null };
    var meta = document.querySelector('meta[name="hh-wishlist-stats"]');
    var url = meta ? meta.getAttribute('content') || '' : '';
    if (!url) return info;

    var choice = /[?&]hh-count=(on|off)(?:&|$)/.exec((window.location && window.location.search) || '');
    try {
      if (choice && choice[1] === 'off') window.localStorage.setItem(STATS_MUTE_KEY, '1');
      if (choice && choice[1] === 'on') window.localStorage.removeItem(STATS_MUTE_KEY);
      info.muted = window.localStorage.getItem(STATS_MUTE_KEY) === '1';
    } catch (e) {
      // Storage blocked: the choice holds for this page only
      info.muted = !!choice && choice[1] === 'off';
    }
    if (!info.muted && !(window.Shopify && window.Shopify.designMode)) info.url = url;
    return info;
  })();

  function daysSince(seconds) {
    return Math.max(0, Math.floor((now() - seconds) / 86400));
  }

  // What every report says about the page it comes from: the market, the kind of device, and whether
  // this is the published theme and a browser driven by a person
  function reportContext() {
    var shop = window.Shopify || {};
    var screen = window.screen || {};
    var touch = !!window.matchMedia && window.matchMedia('(hover: none)').matches;
    var small = Math.min(screen.width || 9999, screen.height || 9999) < 600;
    var context = {
      m: typeof shop.country === 'string' ? shop.country : '',
      d: !touch ? 'desktop' : small ? 'phone' : 'tablet',
    };
    if (shop.theme && shop.theme.role && shop.theme.role !== 'main') context.t = 1;
    if (window.navigator && window.navigator.webdriver) context.w = 1;
    return context;
  }

  // Queues one report; reports made in a row travel together.
  //   save | remove | cart: p, v, h, c, s = the item, src = card | product | wishlist
  //   n = items on the list afterwards (view: items shown; merge: items that joined the account)
  //   first = 1 on the save that started a list; age = days since the item was saved (view: the oldest)
  function tally(kind, fields) {
    if (!stats.url) return;
    var record = { k: kind };
    Object.keys(fields || {}).forEach(function (name) {
      if (fields[name] !== undefined && fields[name] !== null) record[name] = fields[name];
    });
    stats.queue.push(record);
    if (stats.queue.length > STATS_MAX) stats.queue.shift();
    window.clearTimeout(stats.timer);
    stats.timer = window.setTimeout(report, SEND_DELAY_MS);
  }

  // One try and no waiting list: a count that does not arrive is lost, and none arrives twice.
  // keepalive lets the request finish when the page is left meanwhile.
  function report() {
    window.clearTimeout(stats.timer);
    if (!stats.url || !stats.queue.length) return;
    var body = JSON.stringify({ e: stats.queue, ctx: reportContext() });
    stats.queue = [];
    try {
      window
        .fetch(stats.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: body,
          credentials: 'same-origin',
          keepalive: true,
        })
        .catch(function () {});
    } catch (e) {
      // A browser without fetch: nothing is counted
    }
  }

  /*
    An order tells Studio which of its items came off a wishlist: when a saved product goes to the cart,
    its variant id joins a cart attribute, which Shopify copies to the order ("Additional details" on the
    order's page in the admin). The shopper does not see it. An id left behind by an item taken out of
    the cart again does no harm: Studio counts the ids that are also on the order.
  */
  var marking = null;

  function markCart(variantId) {
    var routes = window.routes || {};
    var id = String(variantId || '');
    if (!stats.url || !id || !routes.cart_url || !routes.cart_update_url || typeof Promise !== 'function') return;

    // Reads what the cart remembers, adds this id, writes it back
    function write() {
      return window
        .fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (cart) {
          if (!cart) return null;
          var marked = String((cart.attributes && cart.attributes[CART_MARK]) || '')
            .split(',')
            .filter(Boolean);
          if (marked.indexOf(id) !== -1) return null;
          var attributes = {};
          attributes[CART_MARK] = marked.concat([id]).slice(-CART_MARK_MAX).join(',');
          return window.fetch(routes.cart_update_url + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: attributes }),
            credentials: 'same-origin',
            keepalive: true,
          });
        })
        .catch(function () {});
    }

    // One at a time: two items put in the cart in a row would otherwise overwrite each other's id
    marking = (marking || Promise.resolve()).then(write, write);
  }

  /* ---------- the list in this browser ---------- */

  // items: the list; owner: the customer whose account it was last matched with (null = a guest's list);
  // rev: the revision of the account's list it was matched with; pending: changes not yet in the account
  var state = null;

  function load() {
    if (state) return state;
    var data = null;
    try {
      data = JSON.parse(window.localStorage.getItem(KEY) || 'null');
    } catch (e) {
      // Storage blocked or unreadable: the list then lasts for this page only
    }
    data = data && typeof data === 'object' ? data : {};
    state = {
      items: hhWlApply(Array.isArray(data.items) ? data.items : [], [], now()),
      owner: Number(data.owner) > 0 ? Number(data.owner) : null,
      rev: Number(data.rev) > 0 ? Number(data.rev) : 0,
      pending: Array.isArray(data.pending) ? data.pending.slice(-PENDING_MAX) : [],
    };
    return state;
  }

  function announce(change) {
    document.dispatchEvent(new CustomEvent(CHANGE, { detail: change || {} }));
  }

  function commit(change) {
    try {
      window.localStorage.setItem(
        KEY,
        JSON.stringify({ v: 1, items: state.items, owner: state.owner, rev: state.rev, pending: state.pending })
      );
    } catch (e) {
      // See load()
    }
    announce(change);
  }

  // Changes wait for the account when a customer is logged in, and on a device that was matched with an
  // account before. A plain guest's list needs no queue: the whole list joins the account at login.
  function queued() {
    return account.enabled && (account.customer !== null || load().owner !== null);
  }

  function change(op, detail) {
    var current = load();
    current.items = hhWlApply(current.items, [op], now());
    if (queued()) {
      op.i = newId();
      current.pending = current.pending.concat([op]).slice(-PENDING_MAX);
    }
    commit(detail);
    sendSoon(SEND_DELAY_MS);
  }

  var api = {
    key: KEY,
    event: CHANGE,

    items: function () {
      return load().items.slice();
    },

    count: function () {
      return load().items.length;
    },

    // color undefined or null = any colour of the product
    has: function (productId, color) {
      return load().items.some(function (item) {
        return hhWlSame(item, productId, color);
      });
    },

    // Saving a product + colour that is already on the list replaces its entry (new size, back on top).
    // from: where the heart was, 'card' or 'product', for the counter
    add: function (item, from) {
      var op = {
        o: 'add',
        p: Number(item.p),
        h: String(item.h || ''),
        v: Number(item.v),
        c: item.c || '',
        s: item.s ? 1 : 0,
        t: now(),
      };
      var entry = hhWlClean(op, op.t);
      if (!entry) return false;
      var before = load().items.length;
      change(op, { type: 'add', item: entry });
      tally('save', {
        p: entry.p,
        v: entry.v,
        h: entry.h,
        c: entry.c,
        s: entry.s,
        src: from,
        n: load().items.length,
        first: before === 0 ? 1 : null,
      });
      return true;
    },

    // Changes an entry where it stands (another size or colour): patch.v = variant id, patch.s = size
    // chosen or not, patch.c = another colour, refused when that colour is already an entry of its own
    update: function (productId, color, patch) {
      var from = color || '';
      var to = patch.c === undefined || patch.c === null ? from : String(patch.c);
      var items = load().items;
      var found = items.some(function (item) {
        return hhWlSame(item, productId, from);
      });
      var taken =
        to !== from &&
        items.some(function (item) {
          return hhWlSame(item, productId, to);
        });
      if (!found || taken) return false;

      var next = { c: to };
      if (patch.v) next.v = Number(patch.v);
      if (patch.s !== undefined) next.s = patch.s ? 1 : 0;
      change(
        { o: 'update', p: Number(productId), c: from, to: next },
        { type: 'update', productId: Number(productId), color: to, from: from }
      );
      return true;
    },

    // from: where it was taken off, 'card', 'product' or 'wishlist', for the counter; 'gone' = the
    // product no longer exists and left the list by itself, which is not counted
    remove: function (productId, color, from) {
      var which = color === undefined || color === null ? null : color;
      var gone = load().items.filter(function (item) {
        return hhWlSame(item, productId, which);
      });
      if (!gone.length) return false;
      change({ o: 'remove', p: Number(productId), c: which }, { type: 'remove', productId: Number(productId), color: color });
      if (from !== 'gone') {
        var left = load().items.length;
        gone.forEach(function (item) {
          tally('remove', { p: item.p, v: item.v, h: item.h, c: item.c, s: item.s, src: from, n: left, age: daysSince(item.t) });
        });
      }
      return true;
    },

    // A saved product went to the cart, from the wishlist page ('wishlist') or from its own page
    // ('product'): counted, and the cart remembers it for the order. False when it is not a saved product.
    carted: function (productId, variantId, from) {
      var entry = null;
      load().items.forEach(function (item) {
        if (!hhWlSame(item, productId)) return;
        if (!entry || Number(item.v) === Number(variantId)) entry = item;
      });
      if (!entry) return false;
      // Bought in another colour or size than the one saved: the variant is told, the saved colour is not
      var same = Number(entry.v) === Number(variantId);
      tally('cart', {
        p: entry.p,
        v: Number(variantId) || entry.v,
        h: entry.h,
        c: same ? entry.c : null,
        s: same ? entry.s : null,
        src: from,
        age: daysSince(entry.t),
      });
      markCart(variantId || entry.v);
      return true;
    },

    // The wishlist page was opened: how many items it shows, and how long ago the oldest was saved.
    // A page the browser prepares ahead of a click only counts once it is really shown.
    visited: function () {
      if (document.prerendering) {
        document.addEventListener('prerenderingchange', api.visited, { once: true });
        return;
      }
      var items = load().items;
      var oldest = items.reduce(function (first, item) {
        return Math.min(first, item.t);
      }, now());
      tally('view', { n: items.length, age: items.length ? daysSince(oldest) : null });
    },

    // 'on' = counted; 'muted' = the owner asked for this browser not to be counted; 'off' = no counter
    counting: function () {
      return stats.muted ? 'muted' : stats.url ? 'on' : 'off';
    },

    // For the wishlist page: is account saving switched on, who is logged in, where to log in, and
    // saved = the account has everything (false while the relay cannot be reached)
    account: function () {
      return {
        enabled: account.enabled,
        customer: account.customer,
        login: account.login,
        saved: account.customer !== null && !sendFailed,
      };
    },
  };

  window.hhWishlist = api;

  /* ---------- the list in the account ---------- */

  var sending = false;
  var sendTimer = null;
  var retryMs = 0;
  var sendFailed = false;

  // A customer is logged in: this browser's list and the account's list are brought together
  function reconcile() {
    if (!account.enabled || account.customer === null) return;
    var current = load();
    var list = account.list || {};
    var listItems = Array.isArray(list.items) ? list.items : [];
    var listRev = Number(list.rev) > 0 ? Number(list.rev) : 0;
    var applied = Array.isArray(list.seen) ? list.seen : [];

    if (current.owner === null) {
      // First login on this device: what was saved as a guest joins the account's list, each item with
      // its own date, so the newer of two entries for the same product and colour wins. Oldest first,
      // the order in which they were saved.
      if (current.items.length) tally('merge', { n: current.items.length });
      current.pending = current.items
        .slice()
        .reverse()
        .map(function (item) {
          return { o: 'add', p: item.p, h: item.h, v: item.v, c: item.c, s: item.s, t: item.t, i: newId() };
        });
      current.items = hhWlApply(listItems, current.pending, now());
      current.owner = account.customer;
      current.rev = listRev;
    } else if (current.owner === account.customer) {
      // The account's list moved on (another device): it is taken, with what still waits here on top.
      // An older list than the one already known (a page from the browser's cache) is ignored.
      if (listRev > current.rev) {
        current.pending = current.pending.filter(function (op) {
          return applied.indexOf(op.i) === -1;
        });
        current.items = hhWlApply(listItems, current.pending, now());
        current.rev = listRev;
      }
    } else {
      // The copy of another customer: replaced by this customer's list
      current.items = hhWlApply(listItems, [], now());
      current.pending = [];
      current.owner = account.customer;
      current.rev = listRev;
    }
    commit({ type: 'sync' });
    send();
  }

  function sendSoon(delay) {
    if (!account.enabled || account.customer === null) return;
    window.clearTimeout(sendTimer);
    sendTimer = window.setTimeout(send, delay);
  }

  // Sends what waits to the relay. keepalive lets the request finish when the page is left meanwhile.
  function send() {
    window.clearTimeout(sendTimer);
    if (!account.enabled || account.customer === null || !account.url || sending) return;
    var current = load();
    if (current.owner !== account.customer || !current.pending.length) return;

    sending = true;
    var sent = current.pending.slice();
    window
      .fetch(account.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ops: sent }),
        credentials: 'same-origin',
        keepalive: true,
      })
      .then(function (response) {
        // 401: the store no longer sees a logged-in customer. 400, 413: these changes can never be
        // taken as they are. Anything else that is not a success is worth another try later.
        if (response.status === 401) return { loggedOut: true };
        if (response.status === 400 || response.status === 413) return { refused: true };
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (answer) {
        sending = false;
        if (answer && answer.loggedOut) {
          // The changes keep waiting here; the wishlist page asks to log in again
          account.customer = null;
          announce({ type: 'account' });
          return;
        }
        if (!answer || (!answer.refused && (!answer.ok || !Array.isArray(answer.items)))) {
          throw new Error('Unexpected answer');
        }
        retryMs = 0;
        sendFailed = false;
        settle(sent, answer);
      })
      .catch(function () {
        // The relay cannot be reached: the changes stay here and are tried again, less and less often
        sending = false;
        retryMs = Math.min(RETRY_MAX_MS, retryMs ? retryMs * 2 : RETRY_FIRST_MS);
        window.clearTimeout(sendTimer);
        sendTimer = window.setTimeout(send, retryMs);
        if (!sendFailed) {
          sendFailed = true;
          announce({ type: 'account' });
        }
      });
  }

  // The relay answered with the account's list after the changes
  function settle(sent, answer) {
    // Another tab may have written meanwhile: start from what is stored now
    state = null;
    var current = load();
    var ids = sent.map(function (op) {
      return op.i;
    });
    current.pending = current.pending.filter(function (op) {
      return ids.indexOf(op.i) === -1;
    });
    if (!answer.refused && current.owner === account.customer && Number(answer.rev) >= current.rev) {
      current.items = hhWlApply(answer.items, current.pending, now());
      current.rev = Number(answer.rev) || current.rev;
    }
    commit({ type: 'sync' });
    if (current.pending.length) sendSoon(0);
  }

  // The page says nobody is logged in, but that may have changed since it was made: the relay is asked
  // who is logged in now. With an answer the visitor is treated as on a page made for that customer.
  var asking = false;

  function askAccount() {
    if (!account.enabled || !account.url || account.customer !== null || asking) return;
    asking = true;
    window
      .fetch(account.url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (answer) {
        asking = false;
        if (!answer || !answer.ok || !(Number(answer.customer) > 0) || account.customer !== null) return;
        account.customer = Number(answer.customer);
        account.list = { rev: answer.rev, items: answer.items, seen: answer.seen };
        sendFailed = false;
        state = null;
        reconcile();
      })
      .catch(function () {
        asking = false;
      });
  }

  reconcile();

  // The account window of the header (Shopify's <shopify-account>) lets a visitor sign in without the
  // page being loaded again: when it closes, the relay is asked. Its events do not bubble, hence capture.
  document.addEventListener(
    'close',
    function (event) {
      if (event.target && event.target.matches && event.target.matches('shopify-account')) askAccount();
    },
    true
  );

  // Another tab changed the list, or the page came back from the back/forward cache
  window.addEventListener('storage', function (event) {
    if (event.key !== KEY && event.key !== null) return;
    state = null;
    announce({ type: 'sync' });
  });

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    state = null;
    announce({ type: 'sync' });
    send();
    askAccount();
  });

  // Leaving the page or putting it in the background: what waits is sent now
  window.addEventListener('pagehide', send);
  window.addEventListener('pagehide', report);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'hidden') return;
    send();
    report();
  });

  // A saved product bought from its own page: Dawn's product form announces what it put in the cart
  // (assets/product-form.js; pubsub.js and constants.js are deferred scripts loaded before this one)
  if (stats.url && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var line = event && event.source === 'product-form' ? event.cartData : null;
      if (line && line.product_id) api.carted(line.product_id, event.productVariantId || line.variant_id, 'product');
    });
  }

  /* ---------- header count ---------- */

  function paintCount() {
    var count = api.count();
    document.querySelectorAll('[data-hh-wishlist-count]').forEach(function (bubble) {
      bubble.hidden = count === 0;
      var number = bubble.querySelector('[data-hh-wishlist-number]');
      if (number) number.textContent = count < 100 ? String(count) : '99+';
      var spoken = bubble.querySelector('[data-hh-wishlist-spoken]');
      if (spoken) spoken.textContent = count === 1 ? '1 item' : count + ' items';
    });
  }

  document.addEventListener(CHANGE, paintCount);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintCount);
  } else {
    paintCount();
  }

  /* ---------- the heart ---------- */

  /*
    <hh-wishlist-button data-product-id data-handle data-variant-id data-color data-context="card|product">
    A card heart stands for the product whatever the colour; a product-page heart for the colour on show.
  */
  class HhWishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      if (!this.bound) {
        this.bound = true;
        this.onChange = this.paint.bind(this);
        this.button.addEventListener('click', this.onClick.bind(this));
      }
      document.addEventListener(CHANGE, this.onChange);
      this.paint();
    }

    disconnectedCallback() {
      if (this.onChange) document.removeEventListener(CHANGE, this.onChange);
    }

    get isCard() {
      return this.dataset.context !== 'product';
    }

    // The colour that decides whether the heart is on: any colour for a card
    get colorKey() {
      return this.isCard ? null : this.dataset.color || '';
    }

    get isOn() {
      return api.has(this.dataset.productId, this.colorKey);
    }

    onClick(event) {
      // The card under the heart is one large link
      event.preventDefault();
      event.stopPropagation();

      var from = this.isCard ? 'card' : 'product';
      if (this.isOn) {
        api.remove(this.dataset.productId, this.colorKey, from);
        return;
      }

      var added = api.add(
        {
          p: this.dataset.productId,
          h: this.dataset.handle,
          v: this.dataset.variantId,
          c: this.dataset.color || '',
          s: !this.isCard || this.dataset.single === 'true',
        },
        from
      );
      if (!added) return;

      this.classList.add('is-popping');
      window.setTimeout(this.classList.remove.bind(this.classList, 'is-popping'), 350);
    }

    paint() {
      if (!this.button) return;
      var on = this.isOn;
      this.classList.toggle('is-on', on);
      this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = on ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) this.button.setAttribute('aria-label', label);
    }
  }

  if (!customElements.get('hh-wishlist-button')) {
    customElements.define('hh-wishlist-button', HhWishlistButton);
  }

  /* ---------- product page ---------- */

  /*
    <hh-wishlist-product> carries the heart as a <template> and puts two copies on the page: one over
    the gallery and one next to the Add to cart button. The theme settings decide which of the two shows
    on phones and on larger screens (classes read by hh-wishlist.css).

    The gallery heart is a single one for the whole gallery, not one per image: it sits in the gallery's
    frame, which does not move, so on a phone it stays put while the images slide under it, and in a grid
    of images only the first carries it. Its place is measured from the first image on show (written as
    --hh-media-* on the frame) and measured again when the gallery changes: Dawn swaps the images on a
    colour change, the thumbnails switch the image on show, the window is resized.
  */
  class HhWishlistProduct extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('template');
      this.scope = this.closest('product-info');
      if (!this.template || !this.scope) return;

      this.variantId = this.dataset.variantId;
      this.color = this.dataset.color || '';
      this.mount = this.mount.bind(this);
      this.place = this.place.bind(this);
      this.mount();

      var gallery = this.scope.querySelector('media-gallery');
      if (gallery && 'MutationObserver' in window) {
        // class changes: the image on show (is-active); never "style", which place() itself writes
        this.observer = new MutationObserver(this.mount);
        this.observer.observe(gallery, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });
      }
      if (this.viewer && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.place);
        this.resizeObserver.observe(this.viewer);
      }

      // pubsub.js and constants.js are deferred scripts loaded before this one
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.unsubscribe) this.unsubscribe();
    }

    get hearts() {
      return this.scope.querySelectorAll('hh-wishlist-button[data-context="product"]');
    }

    heart(kind) {
      var heart = this.template.content.firstElementChild.cloneNode(true);
      heart.classList.add(
        'hh-heart--' + kind,
        'hh-heart--d-' + this.dataset.desktop,
        'hh-heart--m-' + this.dataset.mobile
      );
      heart.dataset.variantId = this.variantId;
      heart.dataset.color = this.color;
      return heart;
    }

    mount() {
      var viewer = this.scope.querySelector('media-gallery [id^="GalleryViewer"]');
      if (viewer) {
        this.viewer = viewer;
        // Keeps the heart's layer inside the gallery, under the header, its menu and the drawers
        if (!viewer.classList.contains('hh-heart-frame')) viewer.classList.add('hh-heart-frame');
        if (!this.galleryHeart || this.galleryHeart.parentElement !== viewer) {
          this.galleryHeart = this.heart('media');
          viewer.appendChild(this.galleryHeart);
        }
        this.place();
      }

      var buttons = this.scope.querySelector('.product-form__buttons');
      if (buttons && !buttons.querySelector(':scope > hh-wishlist-button')) {
        buttons.appendChild(this.heart('buy'));
        buttons.classList.add('hh-buy--d-' + this.dataset.desktop, 'hh-buy--m-' + this.dataset.mobile);
      }
    }

    // Where the first image on show sits inside the gallery's frame
    place() {
      var viewer = this.viewer;
      var heart = this.galleryHeart;
      if (!viewer || !heart) return;

      var image = null;
      var containers = viewer.querySelectorAll('.product-media-container');
      for (var i = 0; i < containers.length; i++) {
        if (containers[i].offsetParent !== null) {
          image = containers[i];
          break;
        }
      }
      // The class is only touched when it changes: the gallery is watched for class changes (see above)
      if (!image) {
        if (heart.classList.contains('is-placed')) heart.classList.remove('is-placed');
        return;
      }

      // On a phone the images are a sliding row: its scroll is taken out, as if the row were at its start
      var row = viewer.querySelector('.product__media-list');
      var frame = viewer.getBoundingClientRect();
      var box = image.getBoundingClientRect();
      var values = {
        left: box.left - frame.left + (row ? row.scrollLeft : 0),
        top: box.top - frame.top,
        width: box.width,
        height: box.height,
      };
      Object.keys(values).forEach(function (name) {
        viewer.style.setProperty('--hh-media-' + name, Math.round(values[name]) + 'px');
      });
      if (!heart.classList.contains('is-placed')) heart.classList.add('is-placed');
    }

    onVariantChange(event) {
      var data = (event && event.data) || {};
      if (data.sectionId !== this.dataset.sectionId || !data.variant) return;

      var position = Number(this.dataset.colorPosition);
      this.variantId = String(data.variant.id);
      this.color = position > 0 && data.variant.options ? data.variant.options[position - 1] || '' : '';

      this.hearts.forEach(
        function (heart) {
          heart.dataset.variantId = this.variantId;
          heart.dataset.color = this.color;
          if (heart.paint) heart.paint();
        }.bind(this)
      );
    }
  }

  if (!customElements.get('hh-wishlist-product')) {
    customElements.define('hh-wishlist-product', HhWishlistProduct);
  }

  /* ---------- touch screens: a long press on a product card shows its heart ---------- */

  /*
    Theme setting "Heart on product cards, touch screens" = "On a long press" (class on <body>).
    The heart stays until the next touch elsewhere. The press must not open the product when the
    finger lifts, and the phone's own long-press menu on the card is switched off (hh-wishlist.css).
  */
  (function () {
    var PRESS_MS = 450;
    var MOVE_PX = 10;
    var timer = null;
    var start = null;
    var revealed = null;
    var longPressed = false;
    var swallowClickUntil = 0;

    function active() {
      return document.body.classList.contains('hh-wl-touch--longpress');
    }

    function hostOf(target) {
      return target && target.closest ? target.closest('.card-wrapper, .hh-heart-host') : null;
    }

    function hide() {
      if (revealed) revealed.classList.remove('is-revealed');
      revealed = null;
    }

    function cancel() {
      window.clearTimeout(timer);
      timer = null;
    }

    document.addEventListener(
      'touchstart',
      function (event) {
        if (!active() || event.touches.length !== 1) return;
        var host = hostOf(event.target);
        var heart = host && host.querySelector('.hh-heart--card');

        // A touch anywhere but on the heart on show puts it away
        if (revealed && !revealed.contains(event.target)) hide();
        if (!heart || heart.contains(event.target)) return;

        start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        cancel();
        timer = window.setTimeout(function () {
          timer = null;
          longPressed = true;
          heart.classList.add('is-revealed');
          revealed = heart;
          if (navigator.vibrate) navigator.vibrate(10);
        }, PRESS_MS);
      },
      { passive: true }
    );

    document.addEventListener(
      'touchmove',
      function (event) {
        if (!timer || !start) return;
        var touch = event.touches[0];
        if (Math.abs(touch.clientX - start.x) > MOVE_PX || Math.abs(touch.clientY - start.y) > MOVE_PX) cancel();
      },
      { passive: true }
    );

    document.addEventListener(
      'touchend',
      function () {
        cancel();
        if (!longPressed) return;
        longPressed = false;
        swallowClickUntil = Date.now() + 500;
      },
      { passive: true }
    );

    document.addEventListener(
      'touchcancel',
      function () {
        cancel();
        longPressed = false;
      },
      { passive: true }
    );

    // Lifting the finger after a long press would otherwise follow the card's link
    document.addEventListener(
      'click',
      function (event) {
        if (Date.now() > swallowClickUntil) return;
        swallowClickUntil = 0;
        if (revealed && revealed.contains(event.target)) return;
        if (!hostOf(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    document.addEventListener('contextmenu', function (event) {
      if (!active() || !window.matchMedia('(hover: none)').matches) return;
      var host = hostOf(event.target);
      if (host && host.querySelector('.hh-heart--card')) event.preventDefault();
    });
  })();
})();
